/**
 * Choose a corporate partner (M3, Plan D15).
 *
 * Picking from a list is what stops `Infosys`, `infosys` and `Infosys Ltd`
 * accumulating — and once they have, "how many hours did Infosys give us?" has
 * no answer.
 *
 * **There is no "add an organization" here any more.** It used to offer one, so
 * a partner could be created in passing while entering a volunteer. That is
 * exactly how a registry of records other records point at for years fills with
 * near-duplicates: the person adding it is mid-way through a different job, has
 * not looked at what is already in the list, and only needs *a* row to carry on.
 * Partners are maintained deliberately — in NocoDB, or by an admin — and this is
 * a picker.
 *
 * **Both forms that ask the question use this component** — the public
 * registration page and the staff "Add a volunteer" panel. They used to build
 * the list separately from the same endpoint, which produced the same options
 * by coincidence rather than by construction: two copies of the `Name — City`
 * formatter, two places to change, and nothing to stop one of them being
 * changed. The wording around the field differs by audience and is passed in;
 * the list itself has one implementation.
 *
 * It is `/organizations/options`: the active partners, in the order
 * `sort_order` gives them (migration 041). A retired partner is kept because
 * history points at it, but offering it to somebody being enrolled today would
 * put them into a relationship that ended.
 */

import { SelectInput } from '../forms/Field';
import { useOrganizationOptions } from '../../hooks/useOrganizations';

export interface OrganizationPickerProps {
  value: string;
  onChange: (organizationId: string) => void;
  error?: string;
  /** Corporate and group records must have one; an individual may. */
  required?: boolean;
  disabled?: boolean;
  /**
   * The question, in the voice of whoever is reading it. Staff get the column
   * name; a visitor on the public form is asked about themselves. Only the
   * wording varies — the options below it do not.
   */
  label?: string;
  /** What the empty row says. Same rule as `label`. */
  placeholder?: string;
}

export function OrganizationPicker({
  value,
  onChange,
  error,
  required,
  disabled,
  label = 'Organisation',
  placeholder = 'Choose the organisation',
}: OrganizationPickerProps) {
  const organizations = useOrganizationOptions();

  const options = (organizations.data ?? []).map((organization) => ({
    value: organization.organization_id,
    label: organization.city ? `${organization.name} — ${organization.city}` : organization.name,
  }));

  return (
    <SelectInput
      label={label}
      required={required}
      placeholder={organizations.isPending ? 'Loading…' : placeholder}
      options={options}
      value={value}
      error={
        error ??
        (organizations.isError
          ? 'The partner list did not load fully.'
          : undefined)
      }
      disabled={disabled}
      onChange={(event) => onChange(event.target.value)}
    />
  );
}
